import { useState } from 'react';
import { FormikHelpers } from 'formik';
import * as yup from 'yup';
import { isEmpty } from 'lodash';

import FormSubmitStatus from 'components/FormSubmitStatus';
import { Button } from 'components/Input/Button';
import PhoneNumberField from 'components/Input/PhoneNumberField';
import RadioButton from 'components/Input/RadioButtonField';
import InputField from 'components/Input/InputField/InputField';
import SelectableTile from 'components/Input/SelectableTile';

import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';
import WithDelayedAnimation from 'routes/Chat/Messages/WithDelayedAnimation/WithDelayedAnimation';

import { useConversation } from 'hooks/conversation';
import { useCustomer } from 'hooks/customer';
import { useWaitTimes } from 'hooks/useWaitTimes';
import { useVendorCommand } from 'hooks/useVendorCommand';

import { getFirstName, getLastName } from 'utils/name';
import { isLastMessage, isMessageRead } from 'utils/twilio';
import { formatPhoneNumber, unformattedPhoneNumber } from 'utils/phone';

import {
  validationSchemaContactMethod,
  validationSchemaName,
  validationSchemaPhone,
  validationSchemaQuestion,
} from '../formValidations';
import { RatingContactFollowUpProps, RatingContactFollowUpValuesType } from './types';
import { CONTACT_METHOD_TYPE, PROMPTS, SUBMITTED } from './constants';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';
import { MESSAGE_ALIGN } from 'routes/Chat/Messages/constants';
import { ACTIONS } from 'constants/hook';
import { ButtonGroup, RatingContactFollowUpForm, Section, Sections, SubmitSection } from './styles';
import { scrollToMessagesListBottom } from 'utils/message';
import { htmlId } from 'utils/html';
import FormWrapper from 'components/FormWrapper';
import { FORM_IDS } from 'components/FormWrapper/constants';

const validationSchema = yup.object({
  question: validationSchemaQuestion,
  contactMethod: validationSchemaContactMethod,
  name: yup.string().when('contactMethod', {
    is: CONTACT_METHOD_TYPE.TEXT,
    then: validationSchemaName,
  }),

  phone: yup
    .string()
    .when('contactMethod', { is: CONTACT_METHOD_TYPE.TEXT, then: validationSchemaPhone }),
});

/**
 * To force trigger in conversation (see Message Templates section in README for more info):
 * message_template_rating_contact_followup_chat_only_question_form *
 */
const RatingContactFollowUp = (props: RatingContactFollowUpProps) => {
  const { conversationState } = useConversation();
  const { dispatchCustomerAction } = useCustomer();
  const { data, isError, isLoading } = useWaitTimes();
  const { runVendorCommandAsync } = useVendorCommand();

  const FORM_FLOW_STATE = {
    START: 'question',
    END: 'contactMethod',
  };

  const { twilioMessageIndex, form } = props;
  const { isCompleted: previouslyCompleted, info } = form;
  const { name = '', question = '', contact_type, phone_number: phone_number = '' } = info ?? {};

  const [hasClickedNext, setHasClickedNext] = useState(false);
  const [formFlowState, setFormFlowState] = useState(
    previouslyCompleted ? FORM_FLOW_STATE.END : FORM_FLOW_STATE.START,
  );

  const textWaitTime = isLoading
    ? 'Loading...'
    : isError
    ? 'N/A'
    : `${data?.waitTimes.sms.waitTimeInMinutes} minutes`;
  const chatWaitTime = isLoading
    ? 'Loading...'
    : isError
    ? 'N/A'
    : `${data?.waitTimes.chat.waitTimeInMinutes} minutes`;

  const isLatestMessage = isLastMessage(twilioMessageIndex, conversationState.messages.length);
  const read = isMessageRead(twilioMessageIndex, conversationState);
  const animate = isLatestMessage && !read;
  const commonAnimationProps = { animate, showIndicator: true };

  const onClickNext = (values: RatingContactFollowUpValuesType, errors: any) => {
    scrollToMessagesListBottom();
    setHasClickedNext(true);
    if (values.question && isEmpty(errors.question)) setFormFlowState(FORM_FLOW_STATE.END);
  };

  const onSubmit = async (
    values: RatingContactFollowUpValuesType,
    helpers: FormikHelpers<RatingContactFollowUpValuesType>,
  ) => {
    helpers.setSubmitting(true);
    try {
      await runVendorCommandAsync(VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOW_UP_QUESTION, {
        form_id: props.form.id,
        twilio_message_id: conversationState.messages[twilioMessageIndex].sid,
        question: values.question,

        contact_type: values.contactMethod || CONTACT_METHOD_TYPE.CHAT,

        // Values for Text only
        phone_number:
          values.contactMethod === CONTACT_METHOD_TYPE.TEXT
            ? unformattedPhoneNumber(values.phone)
            : '',
        name: values.contactMethod === CONTACT_METHOD_TYPE.TEXT ? values.name : '',
      });
      helpers.setSubmitting(false);
      helpers.setStatus(SUBMITTED);
    } catch (e: any) {
      helpers.setSubmitting(false);
      helpers.setStatus(e.name);
      console.error('Failed submitting rating contact follow-up', e);
    } finally {
      if (contact_type === CONTACT_METHOD_TYPE.TEXT) {
        dispatchCustomerAction({
          type: ACTIONS.CHANGE,
          payload: {
            userInfo: { firstName: getFirstName(values.name), lastName: getLastName(values.name) },
          },
        });
      }
    }
  };

  return (
    <FormWrapper
      id={FORM_IDS.RATING_CONTACT_FOLLOW_UP}
      initialValues={{
        question,
        contactMethod: contact_type || '',
        name,
        phone: formatPhoneNumber(phone_number),
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnBlur={false}
    >
      {({ values, isValid, isValidating, isSubmitting, status, dirty, errors }) => {
        const fieldsDisabled = previouslyCompleted || status === SUBMITTED || isSubmitting;

        const submitDisabled = fieldsDisabled || !isValid || isValidating || !dirty;
        return (
          <RatingContactFollowUpForm noValidate>
            <Sections>
              <Section>
                <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
                  <PlainTextMessage
                    align={MESSAGE_ALIGN.LEFT}
                    hasTopGutter={false}
                    text={PROMPTS.QUESTION_PROMPT}
                  />
                </WithDelayedAnimation>
                <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
                  <InputField
                    id={`question-${twilioMessageIndex}`}
                    name='question'
                    placeholder='Write your question here...'
                    multiline
                    required
                    disabled={hasClickedNext}
                  />
                </WithDelayedAnimation>
                {formFlowState === FORM_FLOW_STATE.START && (
                  <WithDelayedAnimation relativeIndex={2} {...commonAnimationProps}>
                    <ButtonGroup>
                      <Button
                        id={htmlId('btn-rating-contact=follow-up-next')}
                        type='button'
                        disabled={!values.question}
                        onClick={() => onClickNext(values, errors)}
                      >
                        Next
                      </Button>
                    </ButtonGroup>
                  </WithDelayedAnimation>
                )}
              </Section>
              {formFlowState === FORM_FLOW_STATE.END && (
                <Section>
                  <WithDelayedAnimation relativeIndex={2} {...commonAnimationProps}>
                    <PlainTextMessage align='left' hasTopGutter text={PROMPTS.CONTACT_PROMT} />
                  </WithDelayedAnimation>
                  <WithDelayedAnimation relativeIndex={3} {...commonAnimationProps}>
                    <RadioButton.Group name='contactMethod'>
                      <Section>
                        <SelectableTile
                          onClick={() => {
                            scrollToMessagesListBottom();
                          }}
                          selected={values.contactMethod === CONTACT_METHOD_TYPE.TEXT}
                          disabled={fieldsDisabled}
                        >
                          <SelectableTile.Option
                            id={`${CONTACT_METHOD_TYPE.TEXT}-${twilioMessageIndex}`}
                            value={CONTACT_METHOD_TYPE.TEXT}
                            name='contactMethod'
                            title='Text me'
                            description={`Current wait time: ${textWaitTime}`}
                            disabled={fieldsDisabled}
                            checked={values.contactMethod === CONTACT_METHOD_TYPE.TEXT}
                          />
                        </SelectableTile>
                        <SelectableTile
                          selected={values.contactMethod === CONTACT_METHOD_TYPE.CHAT}
                          disabled={fieldsDisabled}
                        >
                          <SelectableTile.Option
                            id={`${CONTACT_METHOD_TYPE.CHAT}-${twilioMessageIndex}`}
                            value={CONTACT_METHOD_TYPE.CHAT}
                            name='contactMethod'
                            title='Chat'
                            checked={values.contactMethod === CONTACT_METHOD_TYPE.CHAT}
                            description={`Current wait time: ${chatWaitTime}`}
                            disabled={fieldsDisabled}
                          />
                        </SelectableTile>
                      </Section>
                    </RadioButton.Group>
                  </WithDelayedAnimation>
                </Section>
              )}

              {values.contactMethod === CONTACT_METHOD_TYPE.TEXT && (
                <Section>
                  <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
                    <PlainTextMessage align='left' hasTopGutter text={PROMPTS.NAME_PHONE_PROMPT} />
                  </WithDelayedAnimation>
                  <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
                    <InputField
                      id={`name-${twilioMessageIndex}`}
                      name='name'
                      placeholder='Name'
                      label='Name'
                      disabled={fieldsDisabled}
                    />
                  </WithDelayedAnimation>
                  <WithDelayedAnimation relativeIndex={2} {...commonAnimationProps}>
                    <PhoneNumberField
                      id={`phone-${twilioMessageIndex}`}
                      name='phone'
                      placeholder='Mobile phone number'
                      label='Mobile phone number'
                      disabled={fieldsDisabled}
                    />
                  </WithDelayedAnimation>
                </Section>
              )}
            </Sections>
            {formFlowState === FORM_FLOW_STATE.END && (
              <SubmitSection>
                {previouslyCompleted || status ? (
                  <FormSubmitStatus isSuccess={previouslyCompleted || status === SUBMITTED} />
                ) : (
                  <div />
                )}
                {!previouslyCompleted && (
                  <WithDelayedAnimation relativeIndex={3} animate={animate} showIndicator={false}>
                    <ButtonGroup>
                      <Button
                        id={htmlId('btn-rating-contact-follow-up-submit')}
                        type='submit'
                        disabled={submitDisabled}
                      >
                        Submit
                      </Button>
                    </ButtonGroup>
                  </WithDelayedAnimation>
                )}
              </SubmitSection>
            )}
          </RatingContactFollowUpForm>
        );
      }}
    </FormWrapper>
  );
};
export default RatingContactFollowUp;
